import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LoginCredentials,
  RegisterData,
  AuthResponse,
  User,
} from '@coffeeplexity/shared';
import { apiClient } from '../client/http-client';
import { queryKeys } from '../utils/query-keys';

export function useLogin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (credentials: LoginCredentials) => apiClient.login(credentials),
    onSuccess: (data: AuthResponse) => {
      // Cache the user data
      queryClient.setQueryData(queryKeys.auth.currentUser, data.user);
      // Invalidate and refetch any user-related queries
      queryClient.invalidateQueries({ queryKey: queryKeys.auth.all });
    },
  });
}

export function useRegister() {
  return useMutation({
    mutationFn: (userData: RegisterData) => apiClient.register(userData),
  });
}

export function useLogout() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => apiClient.logout(),
    onSuccess: () => {
      // Clear all cached data
      queryClient.clear();
      // Clear auth tokens
      apiClient.clearAuthTokens();
    },
  });
}

export function useCurrentUser() {
  return useQuery({
    queryKey: queryKeys.auth.currentUser,
    queryFn: () => apiClient.getCurrentUser(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      // Don't retry if it's an auth error
      if (error?.status === 401) return false;
      return failureCount < 3;
    },
  });
}

export function useRefreshToken() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => apiClient.refreshAuthToken(),
    onSuccess: (data: AuthResponse) => {
      queryClient.setQueryData(queryKeys.auth.currentUser, data.user);
    },
  });
}

export function useRequestPasswordReset() {
  return useMutation({
    mutationFn: (email: string) => apiClient.requestPasswordReset(email),
  });
}

export function useResetPassword() {
  return useMutation({
    mutationFn: ({
      token,
      newPassword,
    }: {
      token: string;
      newPassword: string;
    }) => apiClient.resetPassword(token, newPassword),
  });
}

export function useVerifyEmail() {
  return useMutation({
    mutationFn: (token: string) => apiClient.verifyEmail(token),
  });
}

export function useResendEmailVerification() {
  return useMutation({
    mutationFn: (email: string) => apiClient.resendEmailVerification(email),
  });
}

export function useUpdateProfile() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, userData }: { id: string; userData: Partial<User> }) =>
      apiClient.updateUser(id, userData),
    onSuccess: updatedUser => {
      // Update the current user cache
      queryClient.setQueryData(queryKeys.auth.currentUser, updatedUser);
      // Invalidate user-related queries
      queryClient.invalidateQueries({
        queryKey: queryKeys.users.detail(updatedUser.id),
      });
    },
  });
}

// Custom hook for authentication state
export function useAuth() {
  const { data: user, isLoading, error } = useCurrentUser();
  const loginMutation = useLogin();
  const logoutMutation = useLogout();
  const registerMutation = useRegister();

  return {
    user,
    isAuthenticated: !!user,
    isLoading,
    error,
    login: loginMutation.mutate,
    logout: logoutMutation.mutate,
    register: registerMutation.mutate,
    isLoginLoading: loginMutation.isPending,
    isLogoutLoading: logoutMutation.isPending,
    isRegisterLoading: registerMutation.isPending,
    loginError: loginMutation.error,
    logoutError: logoutMutation.error,
    registerError: registerMutation.error,
  };
}
